import { Trophy, TrendingUp } from 'lucide-react';
import { EmptyState, ErrorState, Progress, Skeleton, SubBar } from '@/design/components';
import { formatDate } from '@/lib/dates';
import { formatILS } from '@/lib/money';
import { targetPercent, trendLabel, useMySales } from './api';

/** המכירות שלי (PRD 5.18.1). */
export function MySalesPage() {
  const sales = useMySales();

  if (sales.isPending) return <Skeleton className="m-4 h-40" />;
  if (sales.isError) return <ErrorState onRetry={() => void sales.refetch()} />;

  const data = sales.data;
  const hasAny = data && (data.this_month > 0 || data.last_month > 0);

  return (
    <div className="grid content-start gap-4">
      <SubBar title="המכירות שלי" subtitle="החודש, מול היעד ומול השיא" />

      <div className="grid gap-4 px-4 pb-8">
        {!hasAny && (
          <EmptyState
            title="אין עדיין נתוני מכירות"
            description="כשיוזנו מכירות על שמך הן יופיעו כאן."
          />
        )}

        {data && hasAny && (
          <>
            <div className="grid gap-1 rounded-lg bg-raised p-4 shadow-card">
              <span className="text-body-sm text-muted">החודש עד היום</span>
              <span className="font-display text-stat font-bold tabular">
                {formatILS(data.this_month)}
              </span>
              {trendLabel(data.this_month, data.last_month) && (
                <span className="flex items-center gap-1 text-body-sm text-muted">
                  <TrendingUp size={16} strokeWidth={1.75} aria-hidden="true" />
                  {trendLabel(data.this_month, data.last_month)} · בחודש שעבר{' '}
                  {formatILS(data.last_month)}
                </span>
              )}
            </div>

            {data.target && (
              <div className="grid gap-2 rounded-md border border-line bg-raised p-3">
                <span className="text-body font-medium">היעד שלך החודש</span>
                <Progress
                  value={Math.min(data.this_month, data.target)}
                  max={data.target}
                  label={`${formatILS(data.this_month)} מתוך ${formatILS(data.target)} (${
                    targetPercent(data.this_month, data.target) ?? 0
                  }%)`}
                />
              </div>
            )}

            <section className="grid gap-2">
              <h2 className="m-0 flex items-center gap-2 font-display text-display-m font-semibold">
                <Trophy size={20} strokeWidth={1.75} aria-hidden="true" />
                השיאים שלך
              </h2>

              {data.best_day && (
                <div className="flex items-center gap-2 rounded-md border border-line bg-raised p-3">
                  <span className="grow text-body">היום הכי טוב</span>
                  <span className="text-label text-muted">{formatDate(data.best_day.date)}</span>
                  <span className="text-title font-medium tabular">
                    {formatILS(data.best_day.amount)}
                  </span>
                </div>
              )}

              {data.best_month && (
                <div className="flex items-center gap-2 rounded-md border border-line bg-raised p-3">
                  <span className="grow text-body">החודש הכי טוב</span>
                  <span className="text-label text-muted">
                    {formatDate(data.best_month.month)}
                  </span>
                  <span className="text-title font-medium tabular">
                    {formatILS(data.best_month.amount)}
                  </span>
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}
